import React, { useState } from 'react';
import AlertBox from './AlertBox';

interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (otp: string, email: string) => Promise<void>;
  bookingReference: string;
  email: string;
  eventTitle: string;
  onRequestOTP: (email: string) => Promise<void>;
}

const CancellationModal: React.FC<CancellationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  bookingReference,
  email,
  eventTitle,
  onRequestOTP
}) => {
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [userEmail, setUserEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleRequestOTP = async () => {
    if (!userEmail.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onRequestOTP(userEmail);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndCancel = async () => {
    if (otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onConfirm(otp, userEmail);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel booking');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('email');
    setUserEmail('');
    setOtp('');
    setError(null);
    onClose();
  };

  const handleResendOTP = async () => {
    setOtp('');
    setError(null);
    await handleRequestOTP();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-t-lg">
          <h2 className="text-xl font-bold">Cancel Booking</h2>
        </div>

        {/* Body */}
        <div className="p-6">
          {error && <AlertBox type="error" message={error} />}

          {step === 'email' ? (
            <div className="space-y-4">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Warning: This action cannot be undone
                    </h3>
                  </div>
                </div>
              </div>

              <p className="text-gray-700">
                To cancel your booking for <strong>{eventTitle}</strong>, please enter your email address:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Booking Reference:</strong> <span className="font-mono">{bookingReference}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                    setError(null);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && userEmail.trim()) {
                      handleRequestOTP();
                    }
                  }}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-500">
                  Enter the email address associated with this booking
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <p className="text-sm text-blue-700">
                  💳 If you paid online, a refund will be automatically processed to your original payment method. It may take 5-10 business days to appear.
                </p>
              </div>

              <p className="text-gray-700 text-sm">
                A verification code will be sent to your email to confirm the cancellation.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <p className="text-sm text-green-700">
                  ✓ Verification code sent to {userEmail}
                </p>
              </div>

              <p className="text-gray-700">
                Enter the 6-digit code sent to <strong>{userEmail}</strong> to confirm cancellation:
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) setOtp(value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && otp.length === 6) {
                      handleVerifyAndCancel();
                    }
                  }}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-500">
                  Code expires in 15 minutes
                </p>
              </div>

              <button
                onClick={handleResendOTP}
                disabled={loading}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:text-gray-400"
              >
                Didn't receive the code? Resend
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex gap-3">
          {step === 'email' ? (
            <>
              <button
                onClick={handleClose}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestOTP}
                disabled={loading || !userEmail.trim()}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-2 px-4 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending Code...' : 'Send Verification Code'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyAndCancel}
                disabled={loading || otp.length !== 6}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-2 px-4 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancellationModal;
